// ApprovedVendors.jsx

import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  useMediaQuery,
  Switch,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  BsArrowUpRight,
  BsArrowRight,
  BsChevronDown,
  BsChevronUp,
} from "react-icons/bs";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import VendorBadge from "../../components/VendorBadge";

// Steps array
const STEPS = [
  <Typography variant="body1" key="step1">
    <strong>Choose a vendor</strong> and open an investment account
  </Typography>,
  <Typography variant="body1" key="step2">
    <strong>Return here</strong> to set your salary deferral
  </Typography>,
  <Typography variant="body1" key="step3">
    <strong>Finished!</strong> Access your account anytime for adjustments.
  </Typography>,
];

// A custom icon for the Stepper circles
const CustomStepIcon = (props) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: "50%",
      backgroundColor: "#041E42",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontWeight: "bold",
      fontSize: "14px",
    }}
  >
    {props.icon}
  </Box>
);

// Utility to decide if a string is a URL
const isUrl = (str) => {
  if (!str) return false;
  str = str.trim();
  
  // Check for http/https/www patterns
  const httpPattern = /^(https?:\/\/|www\.)/i;
  const httpMatch = httpPattern.test(str);
  
  // Check for domain pattern (e.g., espn.com, example.com)
  const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}/;
  const domainMatch = domainPattern.test(str) && !/\s/.test(str);
  
  const result = httpMatch || domainMatch;
  
  if (window.DEBUG_ROUTING) {
    console.log('isUrl Debug:', {
      input: str,
      httpMatch,
      domainMatch,
      result
    });
  }
  
  return result;
};

// Safely prepend https:// if missing
const fixEstablishAccountURL = (url) => {
  // If it already starts with http/https, return as is
  if (url?.startsWith("http")) {
    return url;
  }
  // If it starts with www., add https://
  if (url?.startsWith("www.")) {
    return `https://${url}`;
  }
  // For domain-only URLs (e.g., espn.com), add https://
  if (url && !url.includes("://") && url.includes(".")) {
    return `https://${url}`;
  }
  // For any other URL-like string, add https://
  return `https://${url}`;
};

// Debug function to check URL processing
const debugUrlProcessing = (url, vendorName) => {
  if (window.DEBUG_ROUTING) {
    console.log('URL Processing Debug:', {
      vendorName,
      originalUrl: url,
      isUrl: isUrl(url),
      fixedUrl: fixEstablishAccountURL(url),
      startsWithHttp: url?.startsWith("http"),
      startsWithHttps: url?.startsWith("https"),
      startsWithWww: url?.startsWith("www"),
    });
  }
};

// Hidden form submission helper
const post = (path, params, method = "post") => {
  const form = document.createElement("form");
  form.method = method;
  form.action = path;
  for (const key in params) {
    if (Object.hasOwnProperty.call(params, key)) {
      const hiddenField = document.createElement("input");
      hiddenField.type = "hidden";
      hiddenField.name = key;
      hiddenField.value = params[key];
      form.appendChild(hiddenField);
    }
  }
  document.body.appendChild(form);
  form.submit();
};

export default function ApprovedVendors({
  loading,
  employerId,
  planTypeRaw,
  planType,
  reliusWebFromState,
  approvedVendorsData,
  employerInfoData,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Debug logging function
  const debugLog = (message, data = null) => {
    if (window.DEBUG_ROUTING) {
      if (data) {
        console.log('ApprovedVendors:', message, data);
      } else {
        console.log('ApprovedVendors:', message);
      }
    }
  };

  // Destructure what you need from employerInfoData
  const { name, state } = employerInfoData || {};

  // Guarantee a fallback for reliusWeb
  const finalReliusWeb = reliusWebFromState || "retirement.tcgservices.com";

  // Basic local UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCards, setExpandedCards] = useState({});

  // Get display name with fallback to vendorName
  const getVendorDisplayName = (vendor) => {
    return vendor.display_name && vendor.display_name.trim() !== ''
      ? vendor.display_name
      : vendor.vendorName;
  };

  // Render vendor notes
  const renderVendorNotes = (notes) => {
    if (!notes || notes.trim() === '') return null;

    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.875rem',
          color: 'text.primary',
          fontWeight: 400,
          mt: 1,
          backgroundColor: '#f5f5f5',
          padding: '8px 12px',
          borderRadius: '4px',
          borderLeft: '3px solid #2196f3'
        }}
      >
        {notes}
      </Typography>
    );
  };

  // Clean URL for display - strip protocol and www
  const cleanUrlForDisplay = (url) => {
    if (!url) return url;

    return url
      .replace(/^https?:\/\//, '')  // Remove http:// or https://
      .replace(/^www\./, '');        // Remove www.
  };

  // Filter vendors by search term
  const filteredVendors = (approvedVendorsData || []).filter((vendor) => {
    const displayName = getVendorDisplayName(vendor);
    return displayName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // “Set Contributions” handler → posts hidden form to finalReliusWeb
  const handleSetContributions = (e, vendor) => {
    e.preventDefault();
    
    const formData = {
      LoginButton: "true",
      RANDOMNUM: "",
      PLANID: "",
      PIN: vendor.planPassword, // or fallback
      DROP: "",
    };
    
    // Use external navigation with form data
    if (window.triggerReliusNavigation) {
      window.triggerReliusNavigation(finalReliusWeb, formData);
    } else {
      // Fallback to direct form submission if function not available
      const url = `https://${finalReliusWeb}/enrollinplan.aspx`;
      post(url, formData);
    }
  };

  // ------------- Render a 3-Column Table (Desktop) -------------
  const ThreeColumnTable = () => (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Investment Provider Options</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              Step 1
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              Step 2
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredVendors.length > 0 ? (
            filteredVendors.map((vendor, idx) => (
              <TableRow
                key={idx}
                sx={{
                  '& > td': {
                    paddingTop: '24px',
                    paddingBottom: '24px'
                  }
                }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {getVendorDisplayName(vendor)}
                    </Typography>
                    <VendorBadge show={vendor.roth_accepted} />
                  </Box>
                  {renderVendorNotes(vendor.notes)}
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{vendor.phoneNumber}</Typography>
                  {vendor.webSite && (
                    <Typography variant="body2">
                      <Button
                        variant="text"
                        sx={{
                          fontWeight: "normal",
                          display: "inline",
                          cursor: "pointer",
                          border: "none",
                          background: "none",
                          padding: 0,
                          font: "inherit",
                          color: "text.secondary",
                          textDecoration: "none",
                          textTransform: "none",
                          minWidth: "auto",
                          fontSize: "inherit",
                          "&:hover": {
                            background: "none",
                            textDecoration: "none",
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          debugUrlProcessing(vendor.webSite, vendor.vendorName);
                          const url = fixEstablishAccountURL(vendor.webSite);
                          debugLog('Website clicked', {
                            vendorName: vendor.vendorName,
                            originalUrl: vendor.webSite,
                            fixedUrl: url
                          });

                          // Always use external navigation to avoid iframe issues
                          if (window.triggerExternalVendorLink) {
                            debugLog('Using triggerExternalVendorLink for website');
                            window.triggerExternalVendorLink(url);
                          } else {
                            // Fallback to direct navigation
                            debugLog('Using direct window.open fallback for website');
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        {cleanUrlForDisplay(vendor.webSite)} <BsArrowUpRight />
                      </Button>
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 700,
                      letterSpacing: 1,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    STEP 1
                  </Typography>
                  <Button
                    variant="contained"
                    disabled={!vendor.establishAccount || !isUrl(vendor.establishAccount)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                    onClick={(e) => {
                      if (!vendor.establishAccount || !isUrl(vendor.establishAccount)) return;

                      e.preventDefault();
                      debugUrlProcessing(vendor.establishAccount, vendor.vendorName);
                      const url = fixEstablishAccountURL(vendor.establishAccount);
                      debugLog('Open Account clicked', {
                        vendorName: vendor.vendorName,
                        originalUrl: vendor.establishAccount,
                        fixedUrl: url
                      });

                      // Always use external navigation to avoid iframe issues
                      if (window.triggerExternalVendorLink) {
                        debugLog('Using triggerExternalVendorLink');
                        window.triggerExternalVendorLink(url);
                      } else {
                        // Fallback to direct navigation
                        debugLog('Using direct window.open fallback');
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    endIcon={<BsArrowUpRight />}
                  >
                    Open Account
                  </Button>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 700,
                      letterSpacing: 1,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    STEP 2
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                    onClick={(e) => handleSetContributions(e, vendor)}
                    endIcon={<BsArrowRight />}
                  >
                    Set Contributions
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <Typography variant="body1">No vendors found.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ------------- Render collapsible Cards (Mobile) -------------
  const MobileVendorCards = () => {
    const toggleCard = (index) => {
      setExpandedCards((prev) => ({
        ...prev,
        [index]: !prev[index],
      }));
    };

    const handleSwitch = (e) => {
      const checked = e.target.checked;
      const newState = {};
      filteredVendors.forEach((_, index) => {
        newState[index] = checked;
      });
      setExpandedCards(newState);
    };

    const allExpanded =
      filteredVendors.length > 0 &&
      filteredVendors.every((_, index) => expandedCards[index]);

    return (
      <>
        <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
          <Switch checked={allExpanded} onChange={handleSwitch} />
          <Typography variant="body2" ml={1}>
            Expand
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {filteredVendors.map((vendor, index) => {
            const isExpanded = expandedCards[index];
            return (
              <Grid item xs={12} key={index}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 1,
                    transition: "all 0.3s",
                    '&:hover': {
                      boxShadow: 3
                    }
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      "&:last-child": { paddingBottom: 3 },
                    }}
                  >
                    {/* Header row */}
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      onClick={() => toggleCard(index)}
                      sx={{ cursor: "pointer" }}
                    >
                      <Grid item>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {getVendorDisplayName(vendor)}
                          </Typography>
                          <VendorBadge show={vendor.roth_accepted} />
                        </Box>
                        {renderVendorNotes(vendor.notes)}
                      </Grid>
                      <Grid item>
                        {isExpanded ? (
                          <BsChevronUp size={18} />
                        ) : (
                          <BsChevronDown size={18} />
                        )}
                      </Grid>
                    </Grid>

                    {/* Expanded content */}
                    {isExpanded && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                          {vendor.phoneNumber}
                        </Typography>
                        {vendor.webSite && (
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            <Button
                              variant="text"
                              sx={{
                                fontWeight: "normal",
                                display: "inline",
                                cursor: "pointer",
                                border: "none",
                                background: "none",
                                padding: 0,
                                font: "inherit",
                                color: "text.secondary",
                                textDecoration: "none",
                                textTransform: "none",
                                minWidth: "auto",
                                fontSize: "inherit",
                                "&:hover": {
                                  background: "none",
                                  textDecoration: "none",
                                }
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                debugUrlProcessing(vendor.webSite, vendor.vendorName);
                                const url = fixEstablishAccountURL(vendor.webSite);
                                debugLog('Website clicked (mobile)', {
                                  vendorName: vendor.vendorName,
                                  originalUrl: vendor.webSite,
                                  fixedUrl: url
                                });

                                // Always use external navigation to avoid iframe issues
                                if (window.triggerExternalVendorLink) {
                                  debugLog('Using triggerExternalVendorLink for website (mobile)');
                                  window.triggerExternalVendorLink(url);
                                } else {
                                  // Fallback to direct navigation
                                  debugLog('Using direct window.open fallback for website (mobile)');
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                }
                              }}
                            >
                              {cleanUrlForDisplay(vendor.webSite)} <BsArrowUpRight />
                            </Button>
                          </Typography>
                        )}

                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="overline"
                            sx={{
                              color: 'text.secondary',
                              fontWeight: 700,
                              letterSpacing: 1,
                              display: 'block',
                              mb: 1
                            }}
                          >
                            STEP 1
                          </Typography>
                          <Button
                            variant="contained"
                            fullWidth
                            disabled={!vendor.establishAccount || !isUrl(vendor.establishAccount)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 500,
                            }}
                            onClick={(e) => {
                              if (!vendor.establishAccount || !isUrl(vendor.establishAccount)) return;

                              e.preventDefault();
                              debugUrlProcessing(vendor.establishAccount, vendor.vendorName);
                              const url = fixEstablishAccountURL(vendor.establishAccount);
                              debugLog('Open Account clicked (mobile)', {
                                vendorName: vendor.vendorName,
                                originalUrl: vendor.establishAccount,
                                fixedUrl: url
                              });

                              if (window.triggerExternalVendorLink) {
                                debugLog('Using triggerExternalVendorLink');
                                window.triggerExternalVendorLink(url);
                              } else {
                                debugLog('Using direct window.open fallback');
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            endIcon={<BsArrowUpRight />}
                          >
                            Open Account
                          </Button>
                        </Box>

                        <Box>
                          <Typography
                            variant="overline"
                            sx={{
                              color: 'text.secondary',
                              fontWeight: 700,
                              letterSpacing: 1,
                              display: 'block',
                              mb: 1
                            }}
                          >
                            STEP 2
                          </Typography>
                          <Button
                            variant="outlined"
                            fullWidth
                            sx={{
                              textTransform: "none",
                              fontWeight: 500,
                            }}
                            onClick={(e) => handleSetContributions(e, vendor)}
                            endIcon={<BsArrowRight />}
                          >
                            Set Contributions
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </>
    );
  };

  // ------------- Return the UI -------------
  return (
    <Container maxWidth="md" sx={{ mt: isMobile ? 2 : 4 }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Grid container direction="column" spacing={3}>
          {/* Stepper */}
          <Grid item>
            <Stepper
              activeStep={-1}
              orientation={isMobile ? "vertical" : "horizontal"}
              sx={{ mb: 4 }}
              alternativeLabel={!isMobile}
            >
              {STEPS.map((label, index) => (
                <Step key={index} active>
                  <StepLabel StepIconComponent={CustomStepIcon}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>

          {/* Desktop: TASBO Link for Texas */}
          {!isMobile && state === "Texas" && (
            <Grid item>
              <Typography variant="body1">
                <Link
                  href="https://www.tasbo.org/news/403b-txed-guide"
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                >
                  TASBO {planTypeRaw} Investment Provider Guide
                </Link>
              </Typography>
            </Grid>
          )}

          {/* Desktop: Header Row */}
          {!isMobile && (
            <Grid item>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                }}
              >
                {name}
              </Typography>
            </Grid>
          )}

          {/* Desktop: Full-Width Search Bar */}
          {!isMobile && (
            <Grid item>
              <Box sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: '#f2f2f2',
                zIndex: 10,
                paddingY: 2,
                borderBottom: '2px solid #e0e0e0'
              }}>
                <TextField
                  label="Search Investment Provider"
                  variant="outlined"
                  fullWidth
                  size="medium"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    ),
                    endAdornment: searchQuery && (
                      <IconButton onClick={() => setSearchQuery("")}>
                        <ClearIcon />
                      </IconButton>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 'bold',
                      color: 'black',
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.1rem',
                      backgroundColor: '#f2f2f2',
                      '& fieldset': {
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>
            </Grid>
          )}

          {/* Mobile: TASBO Link */}
          {isMobile && state === "Texas" && (
            <Grid item>
              <Typography variant="body1">
                <Link
                  href="https://www.tasbo.org/news/403b-txed-guide"
                  target="_blank"
                  rel="noopener"
                  underline="hover"
                >
                  TASBO {planTypeRaw} Investment Provider Guide
                </Link>
              </Typography>
            </Grid>
          )}

          {/* Mobile: Full-Width Search Bar */}
          {isMobile && (
            <Grid item>
              <Box sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: '#f2f2f2',
                zIndex: 10,
                paddingY: 2,
                borderBottom: '2px solid #e0e0e0'
              }}>
                <TextField
                  label="Search Investment Provider"
                  variant="outlined"
                  fullWidth
                  size="medium"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    ),
                    endAdornment: searchQuery && (
                      <IconButton onClick={() => setSearchQuery("")}>
                        <ClearIcon />
                      </IconButton>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 'bold',
                      color: 'black',
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.1rem',
                      backgroundColor: '#f2f2f2',
                      '& fieldset': {
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>
            </Grid>
          )}

          {/* Vendor Listing */}
          <Grid item>
            {isMobile ? <MobileVendorCards /> : <ThreeColumnTable />}
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
